import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { quoteAccommodationFormDefault } from "../../../factory/form/form.default";
import { QuotationFormSchema } from "../../../factory/form/form.rule";
import { GetHotelModel } from "../../../factory/model/query-model-hotel";
import { GetRoomTypeModel } from "../../../factory/model/query-model-room-type";
import { useQyGetHotel } from "../../../factory/query/hotel.query";
import { useQyGetRoomType } from "../../../factory/query/room-type.query";
import { LabelValue } from "../../../shared/models/label-value.interface";
import DropdownControl from "../../../shared/ui/hook-form/dropdown-control/dropdown-control";
import InputDigitControl from "../../../shared/ui/hook-form/input-digit-control/input-digit-control";

type InputFormSchema = QuotationFormSchema;
interface HotelRoomMap {
  [hotelCode: string]: LabelValue[];
}
export default function QuoteAccommodation() {
  const { control, setValue, watch, getValues } =
    useFormContext<QuotationFormSchema>();
  const [hotels, setHotels] = useState<LabelValue[]>([]);
  const [unmappedHotels, setUnmappedHotels] = useState<GetHotelModel[]>([]);
  const [roomTypes, setRoomTypes] = useState<HotelRoomMap>({});
  const [unmappedRoomTypes, setUnmappedRoomTypes] = useState<
    GetRoomTypeModel[]
  >([]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "accommodation",
  });
  const numNights = watch("numNights");
  const nightOptions = numNights
    ? Array.from({ length: numNights }, (_, i) => ({
        label: `Night ${i + 1}`,
        value: i + 1,
      }))
    : [];


  // THIS IS THE LOCAL QUERY DECLARATION
  useQyGetHotel("", 9999, 0, undefined, undefined, true, (response) => {
    const mappedSuggestions = (response?.data || [])
      .map((item) => ({
        label: `${item.name}`,
        value: item.code,
      }))
      .filter((x) => !!x.label);
    // Set the suggestions for the dropdown control
    setUnmappedHotels(response?.data || []);
    setHotels(mappedSuggestions);
  });
  useQyGetRoomType("", 9999, 0, undefined, undefined, true, (response) => {
    const mappedSuggestions = (response?.data || [])
      .map((item) => ({
        hotel: item.hotel,
        label: `${item.name}`,
        value: item.code,
      }))
      .filter((x) => !!x.label);
    setUnmappedRoomTypes(response?.data || []);
    // Set the suggestions for the dropdown control
    const groupedRoomTypes = groupByHotel(mappedSuggestions);
    setRoomTypes(groupedRoomTypes);
  });

  // THIS ARE THE LOCAL FUNCTIONS
  const groupByHotel = (
    mappedSuggestions: Array<{ hotel: string; label: string; value: string }>
  ) => {
    return mappedSuggestions.reduce((acc: HotelRoomMap, item) => {
      const hotelUuid = item.hotel;

      if (!acc[hotelUuid]) {
        acc[hotelUuid] = [];
      }

      acc[hotelUuid].push({
        label: item.label,
        value: item.value,
      });

      return acc;
    }, {});
  };

  return (
    <div className="quotation-accommodation bg-white p-4 rounded shadow my-4">
      <DataTable value={fields} selectionMode="single" className="mb-4">
        <Column
          field="night"
          header="Night"
          body={(item, options) => (
            <DropdownControl<InputFormSchema>
              control={control}
              name={`accommodation.${options.rowIndex}.night`}
              label="Night"
              className="w-full"
              containerClassName="pb-2"
              placeholder="Night"
              options={nightOptions}
            />
          )}
        ></Column>
        <Column
          field="hotel"
          header="Hotel"
          body={(item, options) => (
            <DropdownControl<InputFormSchema>
              control={control}
              name={`accommodation.${options.rowIndex}.hotel`}
              label="Hotel"
              className="w-full"
              containerClassName="pb-2"
              placeholder="Select a hotel"
              options={hotels}
              onSelect={(value) => {
                const hotel = unmappedHotels.find(
                  (hotel) => hotel.code === value
                );
                setValue(
                  `accommodation.${options.rowIndex}.withBreakfast`,
                  hotel?.with_breakfast ? true : false
                );
                setValue(
                  `accommodation.${options.rowIndex}.hotelName`,
                  hotel?.name || ""
                );
              }}
            />
          )}
        ></Column>
        <Column
          field="roomType"
          header="Room Type"
          body={(item, options) => {
            const hotelCode = watch(`accommodation.${options.rowIndex}.hotel`);
            return (
              <DropdownControl<InputFormSchema>
                control={control}
                name={`accommodation.${options.rowIndex}.roomType`}
                label="Room Type"
                className="w-full"
                containerClassName="pb-2"
                placeholder="Select a room type"
                options={roomTypes[hotelCode || ""] || []}
                onSelect={(value) => {
                  const roomType = unmappedRoomTypes.find(
                    (rt) => rt.code === value
                  );
                  setValue(
                    `accommodation.${options.rowIndex}.price`,
                    roomType?.price || 0
                  );
                  setValue(
                    `accommodation.${options.rowIndex}.roomTypeName`,
                    roomType?.name || ""
                  );
                  setValue(
                    `accommodation.${options.rowIndex}.validFrom`,
                    roomType?.valid_from
                  );
                  setValue(
                    `accommodation.${options.rowIndex}.validUntil`,
                    roomType?.valid_until
                  );
                  setValue(
                    `accommodation.${options.rowIndex}.numPeople`,
                    (getValues("numAdults") || 0) +
                      (getValues("numChildren") || 0) || 0
                  );
                }}
              />
            );
          }}
        ></Column>
        <Column
          field="with_breakfast"
          header="With Breakfast"
          sortable
          body={(item, options) => {
            const withBreakfast = watch(
              `accommodation.${options.rowIndex}.withBreakfast`
            );
            return (
              <Checkbox
                checked={withBreakfast ? true : false}
                disabled
                className="p-checkbox-sm"
              />
            );
          }}
        ></Column>
        <Column
          field="price"
          header="Price"
          body={(item, options) => {
            return (
              <InputDigitControl<InputFormSchema>
                control={control}
                name={`accommodation.${options.rowIndex}.price`}
                label="Price"
                className="w-full"
                containerClassName="pb-2"
                placeholder="Select a price"
              />
            );
          }}
        ></Column>
        <Column
          field="actions"
          header="Actions"
          body={(item, options) => (
            <Button
              severity="danger"
              type="button"
              className="mt-2"
              onClick={() => remove(options.rowIndex)}
              icon="pi pi-trash"
            ></Button>
          )}
        ></Column>
      </DataTable>
      <Button
        type="button"
        onClick={() => append(quoteAccommodationFormDefault)}
      >
        Add Accommodation
      </Button>
    </div>
  );
}
